import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { getApiClient } from '../api_client/get';

const normalizeSlug = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim().toLowerCase();
  return trimmed || null;
};

const ExperiencesContext = createContext(null);

export const ExperiencesProvider = ({ children }) => {
  const cacheRef = useRef(new Map());

  const storeExperience = useCallback((experience) => {
    if (!experience) return;
    if (experience.id != null) {
      cacheRef.current.set(`id:${experience.id}`, experience);
    }
    const normalizedSlug = normalizeSlug(experience.slug);
    if (normalizedSlug) {
      cacheRef.current.set(`slug:${normalizedSlug}`, experience);
    }
  }, []);

  const getCachedExperience = useCallback(({ experienceId, experienceSlug } = {}) => {
    const candidates = [];
    if (experienceId != null) candidates.push(`id:${experienceId}`);
    const normalizedSlug = normalizeSlug(experienceSlug);
    if (normalizedSlug) candidates.push(`slug:${normalizedSlug}`);
    for (const key of candidates) {
      if (cacheRef.current.has(key)) {
        return cacheRef.current.get(key);
      }
    }
    return null;
  }, []);

  const loadExperience = useCallback(async ({
    bypassCache = false,
    ...args
  }) => {
    if (!bypassCache) {
      const cached = getCachedExperience(args);
      if (cached) {
        return { success: true, experience: cached };
      }
    }
    const { data } = await getApiClient().fetchExperienceDetail(args);
    if (data?.success && data.experience) {
      storeExperience(data.experience);
    }
    return data;
  }, [getCachedExperience, storeExperience]);

  const value = useMemo(() => ({
    getCachedExperience,
    loadExperience,
    storeExperience
  }), [getCachedExperience, loadExperience, storeExperience]);

  return (
    <ExperiencesContext.Provider value={value}>
      {children}
    </ExperiencesContext.Provider>
  );
};

export const useExperiences = () => {
  const context = useContext(ExperiencesContext);
  if (!context) throw new Error('useExperiences must be used within an ExperiencesProvider');
  return context;
};
